import { useCallback, useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { getAdminTables } from '../../lib/api';
import { Button } from '../../components/Button';
import { TableFormDialog } from './TableFormDialog';
import { PS, T } from '../../lib/theme';
import type { AdminTable } from '../../lib/types';

export function TablesPage() {
  const loader = useCallback((signal: AbortSignal) => getAdminTables(signal), []);
  const { data, error, loading, refetch } = useApi<AdminTable[]>(loader);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AdminTable | null>(null);

  const sorted = useMemo(
    () => [...(data ?? [])].sort((a, b) => a.display_order - b.display_order),
    [data],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ ...T.sectionTitle, margin: 0 }}>Tables</h1>
        <Button
          onClick={() => {
            setEditing(null);
            setDialogOpen(true);
          }}
        >
          New table
        </Button>
      </header>

      {loading && <div style={{ ...T.caption, color: PS.textSec }}>Loading…</div>}
      {error && (
        <div
          style={{
            padding: 12,
            background: PS.errorBg,
            border: `1px solid ${PS.errorBorder}`,
            color: PS.errorText,
            borderRadius: 3,
          }}
        >
          {error.message}
        </div>
      )}

      {!loading && !error && (
        <div
          style={{
            background: PS.white,
            border: `1px solid ${PS.borderLight}`,
            borderRadius: 4,
            overflowX: 'auto',
            // overflow-x:auto implicitly makes overflow-y `auto`, which traps
            // vertical page scroll on touch. Pin it to hidden (height fits).
            overflowY: 'hidden',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${PS.borderLight}` }}>
                <th style={th}>ID</th>
                <th style={th}>Name</th>
                <th style={th}>Order</th>
                <th style={th}>Active</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((t) => (
                <tr
                  key={t.id}
                  onClick={() => {
                    setEditing(t);
                    setDialogOpen(true);
                  }}
                  style={{ borderBottom: `1px solid ${PS.borderLight}`, cursor: 'pointer' }}
                >
                  <td style={td}>#{t.id}</td>
                  <td style={td}>{t.name}</td>
                  <td style={td}>{t.display_order}</td>
                  <td style={td}>{t.is_active ? 'yes' : 'no'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p style={{ ...T.caption, color: PS.textSec }}>
        Click a row to edit. Inactive tables are listed here but hidden from the public booking grid.
      </p>

      <TableFormDialog
        open={dialogOpen}
        table={editing}
        onClose={() => setDialogOpen(false)}
        onSaved={() => void refetch()}
      />
    </div>
  );
}

const th = {
  padding: '10px 12px',
  textAlign: 'left' as const,
  fontFamily: "'Barlow Condensed',sans-serif",
  fontSize: 12,
  letterSpacing: '1px',
  textTransform: 'uppercase' as const,
  color: PS.textSec,
};
const td = {
  padding: '10px 12px',
  fontFamily: "'Barlow',sans-serif",
  fontSize: 16,
  color: PS.nearBlack,
};
